import React, { useEffect } from 'react';

import { Add } from '@mui/icons-material';
import { Grid } from '@mui/material';

import { PickingBySKUsDetails, PickingListBySKUs } from 'commons/types';
import { postRemoveBatch } from 'redux-stores/actions';

import { IPickProcessState } from '../interfaces';
import S from '../PickItemPage.style';

import PickingProcessForm from './PickProcessForm.component';

interface IPickingProcessProps {
  pickList: PickingListBySKUs;
  skuLowStockTriggered: { [K: string]: boolean };
  setIsAllBasketVerified: (isAllBasketVerified: boolean) => void;
  onModalAddBasketOpen: (detail: PickingBySKUsDetails) => void;
  pickProcessState: Map<string, IPickProcessState>;
  setPickProcessState: (state: Map<string, IPickProcessState>) => void;
  // barcodeVerified: boolean;
}

const PickingProcess: React.FC<IPickingProcessProps> = ({
  pickList,
  skuLowStockTriggered,
  setIsAllBasketVerified,
  onModalAddBasketOpen,
  pickProcessState,
  setPickProcessState,
  // barcodeVerified,
}) => {
  const recommendationTotalQty = pickList.pick_recommendation.reduce(
    (total, rec) => total + rec.quantity,
    0,
  );
  const isStockOut =
    !skuLowStockTriggered[pickList.sku_code] &&
    recommendationTotalQty < pickList.total_quantity;

  // states
  useEffect(() => {
    const initPickProcessState = (): Map<string, IPickProcessState> => {
      const result = new Map();
      const defaultPickingInfo = (
        basket: number,
        poNumber: string,
      ): IPickProcessState => ({
        poNumber,
        isOpen: true,
        picked: [
          {
            basket,
            bin: '',
            inventory_number: '',
            expiry_date: '',
            quantity: 0,
            is_verified: false,
          },
        ],
      });

      pickList?.details.forEach((detail) =>
        detail.baskets.forEach((basket) => {
          const _sku = pickList.sku_code;
          const _key = `${detail.po_number}-${_sku}-${basket}`;
          // check existing data. if no data picked yet, use defaultPickingInfo
          const existingPickedData = detail.picked_actual.filter(
            (picked) => picked.basket === basket,
          );
          if (existingPickedData.length > 0) {
            result.set(_key, {
              isOpen: true,
              picked: existingPickedData,
              quantity: detail.quantity,
              poNumber: detail.po_number,
            });
          } else {
            result.set(_key, defaultPickingInfo(basket, detail.po_number));
          }
        }),
      );
      return result;
    };
    setPickProcessState(initPickProcessState());
  }, [pickList?.details, pickList?.sku_code, setPickProcessState]);

  const renderPickProcessRow = (
    skuCode: string,
    detail: PickingBySKUsDetails,
  ): React.JSX.Element[] => {
    const basketProgress = detail.baskets.reduce((sum, basketNumber) => {
      const sku = pickList.sku_code;
      const key = `${detail.po_number}-${sku}-${basketNumber}`;
      const state = pickProcessState.get(key);
      if (!state) return sum;
      return state.picked.reduce((sum1, pick) => pick.quantity + sum1, 0) + sum;
    }, 0);

    return detail.baskets.map((basketNumber, idx) => {
      const sku = pickList.sku_code;
      const key = `${detail.po_number}-${sku}-${basketNumber}`;
      const state = pickProcessState.get(key);
      if (!state) return <div />;

      const maxQuantityToAdd =
        detail.quantity -
        detail.baskets
          .filter((basketCheck) => basketCheck !== basketNumber)
          .reduce((sum, num) => {
            const _key = `${detail.po_number}-${sku}-${num}`;
            const _state = pickProcessState.get(_key);
            if (!_state) return sum;
            return (
              _state.picked.reduce((sum1, pick) => pick.quantity + sum1, 0) +
              sum
            );
          }, 0);

      const onContainerClicked = (): void => {
        const clonedMap = new Map(
          pickProcessState.set(key, { ...state, isOpen: !state.isOpen }),
        );
        setPickProcessState(clonedMap);
      };

      const addBatch = (): void => {
        const updatedPicked = [
          ...state.picked,
          {
            basket: basketNumber,
            bin: '',
            inventory_number: '',
            expiry_date: '',
            quantity: 0,
            is_verified: false,
          },
        ];

        setPickProcessState(
          new Map(
            pickProcessState.set(key, { ...state, picked: updatedPicked }),
          ),
        );
      };

      const removeBatch = (): void => {
        const pickingInfo = state.picked.pop();
        if (pickingInfo) {
          const { poNumber } = state;
          const {
            bin,
            inventory_number: inventoryNumber,
            basket,
          } = pickingInfo;
          postRemoveBatch({
            binNumber: bin,
            inventoryNumber,
            poNumber,
            skuCode,
            basket,
          });
        }

        setPickProcessState(new Map(pickProcessState.set(key, { ...state })));
      };

      return (
        <S.PickProcessWrapper>
          <S.PickProcessGridContainer container onClick={onContainerClicked}>
            <Grid item xs={1}>
              <S.PickProcessArrow className={state.isOpen ? 'open' : 'close'} />
            </Grid>
            <S.PickProcessTitleText item xs={11}>
              <Grid container>
                <Grid item xs={idx === 0 ? 6 : 12}>
                  <p data-testid={`TextItemBasket-${idx + 1}`}>
                    Basket {basketNumber}
                  </p>
                </Grid>
                {idx === 0 && (
                  <Grid item xs={6} style={{ textAlign: 'right' }}>
                    <p>
                      QTY: {basketProgress}/{detail.quantity}
                    </p>
                  </Grid>
                )}
                <Grid xs={6}>
                  <p data-testid={`TextItemPO-${idx + 1}`}>
                    {detail.po_number}
                  </p>
                </Grid>
                <Grid xs={6} style={{ textAlign: 'right' }}>
                  <p>{detail.priority}</p>
                </Grid>
              </Grid>
            </S.PickProcessTitleText>
          </S.PickProcessGridContainer>

          {state.isOpen && (
            <PickingProcessForm
              pickList={pickList}
              state={state}
              stateKey={key}
              skuLowStockTriggered={skuLowStockTriggered}
              setIsAllBasketVerified={setIsAllBasketVerified}
              pickProcessState={pickProcessState}
              setPickProcessState={setPickProcessState}
              maxQuantity={maxQuantityToAdd}
              // barcodeVerified={barcodeVerified}
            />
          )}

          {state.isOpen && (
            <Grid container style={{ marginTop: 8 }}>
              <Grid item xs={6} style={{ textAlign: 'center' }}>
                <S.RedButtonText
                  onClick={() => removeBatch()}
                  disabled={state.picked.length === 1}
                  data-testid={`BtnItemRmvBatch-${idx + 1}`}
                >
                  REMOVE BATCH
                </S.RedButtonText>
              </Grid>
              <Grid item xs={6} style={{ textAlign: 'right' }}>
                <S.BlueButtonText
                  disabled={isStockOut}
                  onClick={addBatch}
                  data-testid={`BtnItemAddBatch-${idx + 1}`}
                >
                  <Add /> ADD BATCH
                </S.BlueButtonText>
              </Grid>
            </Grid>
          )}
        </S.PickProcessWrapper>
      );
    });
  };

  return pickList?.details.map((detail, idx) => (
    <div>
      {renderPickProcessRow(pickList.sku_code, detail)}
      {/* Add Basket Button */}
      <S.AddBasketButtonWrapper>
        <S.SecondaryButton
          disabled={isStockOut}
          onClick={() => onModalAddBasketOpen(detail)}
          data-testid={`BtnItemAddBasket-${idx + 1}`}
        >
          ADD ANOTHER BASKET
        </S.SecondaryButton>
      </S.AddBasketButtonWrapper>
    </div>
  ));
};

export default PickingProcess;
